import {Button} from "primereact/button";
import {ConfirmDialog} from "primereact/confirmdialog";
import {Dialog} from "primereact/dialog";
import {Toast} from "primereact/toast";
import {Tooltip} from "primereact/tooltip";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

import {formatValue} from "@reva/shared/number-format";

import {useRevaTranslation} from "../../hooks/languageHooks";
import {useAreaMeasurement} from "../../hooks/useAreaMeasurement";
import {useBasemapDataYear} from "../../hooks/useBasemapDataYear";
import drawIcon from "../../images/draw_icon.png";
import drawIconWhite from "../../images/draw_icon_white.png";
import {selectAddressData} from "../../redux/addressSlice";
import {selectDarkTheme} from "../../redux/themeSlice";
import {LAYER_ALKIS, LAYER_BASEMAP, LAYER_POIS_DECORATED, selectCapabilities} from "../../redux/wmtsSlice";
import {createLayerSwitcher, createLuftbildLayer, LayerFactory} from "../../util/maps";
import MapLayerUnavailableHint from "../MapLayerUnavailableHint";
import OpenLayersMap from "../OpenLayersMap";

const MAP_CONFIG = {
    zoom: 19,
    minZoom: 15,
    maxZoom: 30,
};

function MeasurementFooter({onCancel, onSave, measuredArea, confirmButton}) {
    const {t} = useTranslation();

    return (
        <div className="flex justify-between items-center">
            <div className="flex items-center">
                <span
                    style={{
                        color: "#6b7280",
                        fontSize: "0.75rem",
                        lineHeight: "1.2",
                        whiteSpace: "pre-line",
                        textAlign: "left",
                    }}
                >
                    {t("measurement.dialogKeyboardShortcuts")}
                </span>
            </div>
            <div>
                <Button
                    className="p-button-text"
                    label={t("general.cancel")}
                    icon="pi pi-times"
                    tabIndex={3}
                    onClick={onCancel}
                />
                <Button
                    ref={confirmButton}
                    className="ml-2"
                    label={t("general.save")}
                    icon="pi pi-check"
                    tabIndex={2}
                    disabled={!measuredArea}
                    onClick={onSave}
                />
            </div>
        </div>
    );
}

function MeasurementConfirmDialog({
    confirmVisible,
    setConfirmVisible,
    confirmButton,
    measuredArea,
    label,
    onConfirm,
    handleCopy,
}) {
    const {t, language} = useRevaTranslation();

    useEffect(() => {
        if (confirmVisible) {
            const focusAcceptButton = () => {
                const buttons = Array.from(document.querySelectorAll(".p-confirm-dialog .p-button"));
                const acceptButton = buttons[buttons.length - 1];
                if (acceptButton) {
                    acceptButton.focus();
                    return true;
                }
                return false;
            };

            if (!focusAcceptButton()) {
                const observer = new MutationObserver(() => {
                    if (focusAcceptButton()) {
                        observer.disconnect();
                    }
                });

                observer.observe(document.body, {
                    childList: true,
                    subtree: true,
                });

                setTimeout(() => observer.disconnect(), 1000);
            }

            const handleKeyDown = (e) => {
                if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                    e.preventDefault();

                    const buttons = Array.from(document.querySelectorAll(".p-confirm-dialog .p-button"));

                    const currentIndex = buttons.indexOf(document.activeElement);

                    if (currentIndex !== -1) {
                        let nextIndex;
                        if (e.key === "ArrowLeft") {
                            nextIndex = currentIndex === 0 ? buttons.length - 1 : currentIndex - 1;
                        } else {
                            nextIndex = currentIndex === buttons.length - 1 ? 0 : currentIndex + 1;
                        }

                        buttons[nextIndex]?.focus();
                    }
                }
            };

            document.addEventListener("keydown", handleKeyDown);

            return () => {
                document.removeEventListener("keydown", handleKeyDown);
            };
        }
    }, [confirmVisible]);

    return (
        <ConfirmDialog
            target={confirmButton.current}
            defaultFocus="accept"
            visible={confirmVisible}
            icon="pi pi-exclamation-triangle"
            closable={false}
            accept={() => {
                onConfirm(measuredArea);
            }}
            message={t("measurement.confirmDialogTitle", {
                area: formatValue(measuredArea, language),
                param: label,
            })}
            acceptLabel={t("general.confirm.yes")}
            rejectLabel={t("general.confirm.no")}
            footer={({acceptClassName, acceptLabel, accept, rejectLabel, rejectClassName, reject}) => (
                <>
                    <Button
                        className={rejectClassName}
                        label={rejectLabel}
                        tabIndex={0}
                        onClick={() => {
                            setConfirmVisible(false);
                        }}
                    />
                    <Button
                        className="p-button-text"
                        label={t("measurement.copy")}
                        icon="pi pi-copy"
                        tabIndex={0}
                        disabled={!measuredArea}
                        onClick={handleCopy}
                    />
                    <Button className={acceptClassName} label={acceptLabel} tabIndex={0} onClick={accept} />
                </>
            )}
        />
    );
}

export function MeasurementDialogIcon({onConfirm, label, disabled}) {
    const [measurementDialogOpen, setMeasurementDialogOpen] = useState(false);
    const [confirmVisible, setConfirmVisible] = useState(false);

    const confirmButton = useRef(null);
    const tooltipRef = useRef(null);
    const toast = useRef(null);

    const {t, language} = useRevaTranslation();
    const addressData = useSelector(selectAddressData);
    const capabilities = useSelector(selectCapabilities);
    const isDarkTheme = useSelector(selectDarkTheme);

    const basemapYear = useBasemapDataYear();
    const alkisCapabilities = capabilities?.[LAYER_ALKIS];
    const basemapCapabilities = capabilities?.[LAYER_BASEMAP];
    const poisCapabilities = capabilities?.[LAYER_POIS_DECORATED];

    const icon = isDarkTheme ? drawIcon : drawIconWhite;
    const addressMeta = addressData?.address;

    const {
        measuredArea,
        lineDistance,
        isFirstLineFixed,
        isFreehandMode,
        drawFunction,
        vectorLayer,
        interactionOptions,
        cancelDrawing,
        removeLastPoint,
        finishDrawing,
        reset,
    } = useAreaMeasurement();

    const alkisMapModel = useMemo(() => {
        return createAlkisMapModel({
            addressMeta,
            addressData,
            alkisCapabilities,
            basemapCapabilities,
            basemapYear,
            poisCapabilities,
            t,
            interactionOptions,
            drawFunction,
            vectorLayer,
        });
    }, [
        addressMeta,
        addressData,
        alkisCapabilities,
        basemapCapabilities,
        basemapYear,
        poisCapabilities,
        t,
        interactionOptions,
        drawFunction,
        vectorLayer,
    ]);

    const handleCopy = useCallback(() => {
        if (measuredArea !== null && measuredArea !== undefined) {
            const formated = formatValue(measuredArea, language);
            navigator.clipboard.writeText(formated);
            toast.current.show({
                severity: "info",
                summary: "Info",
                detail: t("measurement.copyToast", {
                    value: formated,
                }),
                life: 3000,
            });
        }
    }, [measuredArea, language, t]);

    const handleCopyDistance = useCallback(() => {
        if (lineDistance !== null && lineDistance !== undefined) {
            const formated = formatValue(lineDistance, language);
            navigator.clipboard.writeText(formated);
            toast.current.show({
                severity: "info",
                summary: "Info",
                detail: t("measurement.lineCopyToast", {
                    value: formated,
                }),
                life: 3000,
            });
        }
    }, [lineDistance, language, t]);

    const handleEnterKey = useCallback(() => {
        // Finish polygon if drawing and at least 3 points
        if (finishDrawing()) {
            return;
        }

        // Polygon finished and area measured, open confirm dialog
        if (measuredArea && !confirmVisible) {
            setConfirmVisible(true);
        }
    }, [finishDrawing, measuredArea, confirmVisible]);

    useEffect(() => {
        if (!measurementDialogOpen) return;

        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                if (confirmVisible) {
                    setConfirmVisible(false);
                    e.stopPropagation();
                    return;
                }

                if (!cancelDrawing()) {
                    setMeasurementDialogOpen(false);
                }
            }

            if (e.key === "Backspace") {
                e.preventDefault();
                removeLastPoint();
            }

            if (e.key === "Enter") {
                handleEnterKey();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [measurementDialogOpen, cancelDrawing, removeLastPoint, handleEnterKey, confirmVisible]);

    useEffect(() => {
        if (!measurementDialogOpen) {
            reset();
        }
    }, [measurementDialogOpen, reset]);

    return (
        <>
            <Toast ref={toast} position="top-center" />
            <Dialog
                visible={measurementDialogOpen}
                onHide={() => setMeasurementDialogOpen(false)}
                style={{width: "50vw", height: "60vh"}}
                header={
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            paddingRight: "2rem",
                        }}
                    >
                        <div
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "0.5rem",
                            }}
                        >
                            {t("measurement.dialogTitle", {param: label})}
                            {measuredArea && (
                                <>
                                    {" "}
                                    {formatValue(measuredArea, language)} m²
                                    <Button
                                        className="p-button-text"
                                        onClick={handleCopy}
                                        style={{
                                            padding: 1,
                                            minWidth: 30,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <i className="pi pi-copy" style={{fontSize: "1.5rem", lineHeight: 1}} />
                                    </Button>
                                </>
                            )}
                        </div>
                        {lineDistance !== null && !isFreehandMode && (
                            <div
                                style={{
                                    fontSize: "1rem",
                                    fontWeight: "normal",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                }}
                            >
                                {isFirstLineFixed
                                    ? t("measurement.firstLineDistance", {
                                          distance: formatValue(lineDistance, language),
                                      })
                                    : t("measurement.lineDistance", {
                                          distance: formatValue(lineDistance, language),
                                      })}
                                {isFirstLineFixed && (
                                    <Button
                                        className="p-button-text"
                                        onClick={handleCopyDistance}
                                        style={{
                                            padding: 1,
                                            minWidth: 20,
                                            width: "1.5rem",
                                            height: "1.5rem",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <i
                                            className="pi pi-copy"
                                            style={{
                                                fontSize: "1rem",
                                                lineHeight: 1,
                                                color: "#6b7280",
                                            }}
                                        />
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                }
                modal
                closeOnEscape={false}
                footer={
                    <MeasurementFooter
                        onCancel={() => setMeasurementDialogOpen(false)}
                        onSave={() => setConfirmVisible(true)}
                        measuredArea={measuredArea}
                        confirmButton={confirmButton}
                    />
                }
            >
                <MeasurementConfirmDialog
                    confirmVisible={confirmVisible}
                    setConfirmVisible={setConfirmVisible}
                    confirmButton={confirmButton}
                    measuredArea={measuredArea}
                    label={label}
                    onConfirm={(area) => {
                        onConfirm(area);
                        setConfirmVisible(false);
                        setMeasurementDialogOpen(false);
                    }}
                    handleCopy={handleCopy}
                />
                <div className="w-full h-full relative" style={{minHeight: "400px"}}>
                    <MapLayerUnavailableHint layers={[LAYER_ALKIS, LAYER_BASEMAP, LAYER_POIS_DECORATED]} />
                    <OpenLayersMap model={alkisMapModel} />
                </div>
            </Dialog>
            <Button
                ref={tooltipRef}
                className={`p-inputgroup-addon xl:shrink-0`}
                disabled={disabled}
                onClick={() => setMeasurementDialogOpen(true)}
                data-pr-tooltip={t("measurement.tooltip")}
                data-pr-position="top"
            >
                <img alt="draw on" src={icon} style={{width: "1.2rem"}} />
            </Button>
            <Tooltip target={tooltipRef} disabled={disabled} />
        </>
    );
}

function createAlkisMapModel({
    addressMeta,
    addressData,
    alkisCapabilities,
    basemapCapabilities,
    basemapYear,
    poisCapabilities,
    t,
    interactionOptions,
    drawFunction,
    vectorLayer,
}) {
    if (!addressMeta) return null;

    const layers = [];

    // ALKIS Layer
    if (alkisCapabilities) {
        layers.push(
            LayerFactory.createGeoserverWmtsLayer(LAYER_ALKIS, undefined, undefined, "page_address_alkis", undefined, {
                params: {
                    attributions:
                        "<a target='_blank' href='https://www.adv-online.de/AdV-Produkte/Liegenschaftskataster/'>© ALKIS</a>",
                },
                title: "Flurkarte",
                type: "base",
                visible: true,
                ...alkisCapabilities,
            }),
        );
    }

    // Luftbild: Landesluftbild des Bundeslands, sonst ESA (#19684)
    layers.push(createLuftbildLayer(addressMeta.municipality_code, addressData, "measurement_aerial_imagery"));

    // BaseMap Layer
    if (basemapCapabilities) {
        layers.push(
            LayerFactory.createGeoserverWmtsLayer(
                LAYER_BASEMAP,
                undefined,
                undefined,
                "pois_location_basemapde",
                undefined,
                {
                    params: {
                        attributions: `© GeoBasis-DE / <a target="_blank" href="https://www.bkg.bund.de">BKG</a> (${basemapYear}) <a target="_blank" href="https://creativecommons.org/licenses/by/4.0/">CC BY 4.0</a>`,
                    },
                    title: "Basemap",
                    type: "base",
                    // springt als sichtbare Basis ein, wenn die Flurkarte ausfällt (#19906)
                    visible: !alkisCapabilities,
                    ...basemapCapabilities,
                },
            ),
        );
    }

    // OSM Layer
    if (poisCapabilities) {
        layers.push(
            LayerFactory.createGeoserverWmtsLayer(
                LAYER_POIS_DECORATED,
                addressData,
                undefined,
                "pois_location_osm",
                undefined,
                {
                    ...poisCapabilities,
                    params: {
                        attributions:
                            '<a target="_blank" href="https://www.openstreetmap.org/copyright">© OpenStreetMap</a>',
                    },
                    title: "OpenStreetMap",
                    type: "base",
                    visible: !alkisCapabilities && !basemapCapabilities,
                },
            ),
        );
    }

    return {
        view: {
            lat: addressMeta.lat,
            lon: addressMeta.lon,
            ...MAP_CONFIG,
        },
        layers: layers,
        switcher: createLayerSwitcher(t, false, false),
        scaleLine: {enabled: true},
        interaction: {
            enabled: true,
            options: interactionOptions,
            drawFunction: drawFunction,
            layer: vectorLayer,
        },
    };
}
